import React, { useEffect, useState } from "react";

import { Modal, Button, Callout, Badge } from "@commonComponents";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { styled } from "styled-components";

import { colorTheme, gridSpacing, type LHErrorType } from "@utils";

interface ItemContainerProps {
  $isDragging?: boolean;
}

const ItemContainer = styled.div<ItemContainerProps>`
  display: flex;
  align-items: center;
  padding: 12px;
  margin-bottom: 8px;
  background-color: ${(props) =>
    props.$isDragging ? colorTheme("primaryL4") : colorTheme("white")};
  border: 1px solid
    ${(props) =>
      props.$isDragging ? colorTheme("primaryL3") : colorTheme("neutralL4")};
  border-radius: 4px;
  box-shadow: ${(props) =>
    // eslint-disable-next-line color-theme/enforce-color-theme
    props.$isDragging ? "0 2px 4px rgba(0, 0, 0, 0.1)" : "none"};
  cursor: move;
  transition: all 0.2s ease;

  &:hover {
    background-color: #f7fafc;
  }
`;

const PlanName = styled.div`
  flex-grow: 1;
`;

const DragHandleIcon = styled.i`
  color: ${colorTheme("neutralL2")};
  margin-left: ${gridSpacing[4]}px;
  display: flex;
  align-items: center;
`;

const ListContainer = styled.div`
  margin: ${gridSpacing[4]}px 0;
  max-height: 70vh;
  overflow-y: auto;
`;

interface DraggableItemProps {
  id: string;
  index: number;
  moveItem: (dragIndex: number, hoverIndex: number) => void;
  label: string;
}

function DraggableItem({ id, index, moveItem, label }: DraggableItemProps) {
  const [{ isDragging }, drag] = useDrag({
    type: "GENERIC_ITEM",
    item: { id, index },
    collect: (monitor) => {
      return {
        isDragging: monitor.isDragging(),
      };
    },
  });

  const [, drop] = useDrop({
    accept: "GENERIC_ITEM",
    hover(item: { id: string; index: number }) {
      if (!drop) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) {
        return;
      }
      moveItem(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  const ref = React.useRef<HTMLDivElement>(null);
  drag(drop(ref));

  return (
    <ItemContainer ref={ref} $isDragging={isDragging}>
      <Badge mr>{index + 1}</Badge>
      <PlanName>{label}</PlanName>
      <DragHandleIcon className="fa fa-bars" />
    </ItemContainer>
  );
}

interface ReorderModalProps<T> {
  isOpened: boolean;
  setIsOpened: (value: boolean) => void;
  data: T[];
  title: string;
  itemKey: keyof T;
  itemLabel: (item: T) => string;
  save: (reorderedData: T[]) => Promise<void> | void;
  saving?: boolean;
  errors?: LHErrorType<Record<string, unknown>>;
  rankKey?: keyof T;
}

function ReorderModal<T>({
  isOpened,
  setIsOpened,
  data,
  title,
  itemKey,
  itemLabel,
  save,
  saving = false,
  errors = {},
  rankKey = "rank" as keyof T,
}: ReorderModalProps<T>) {
  const [updatedData, setUpdatedData] = useState<T[]>([]);

  useEffect(() => {
    if (isOpened) {
      // Sort by rank initially
      const sortedData = [...data].sort(
        (a, b) => ((a[rankKey] as number) ?? 0) - ((b[rankKey] as number) ?? 0),
      );
      setUpdatedData(sortedData);
    }
  }, [isOpened, data, rankKey]);

  const moveItem = (dragIndex: number, hoverIndex: number) => {
    const draggedItem = updatedData[dragIndex];
    const newItems = [...updatedData];
    newItems.splice(dragIndex, 1);
    newItems.splice(hoverIndex, 0, draggedItem);
    const rankedItems = newItems.map((item, index) => {
      return {
        ...item,
        [rankKey]: index + 1,
      };
    });
    setUpdatedData(rankedItems);
  };

  const handleSave = () => {
    const changedItems = updatedData.filter((updatedItem) => {
      const originalItem = data.find(
        (item) => item[itemKey] === updatedItem[itemKey],
      );
      return originalItem && originalItem[rankKey] !== updatedItem[rankKey];
    });

    if (changedItems.length === 0) {
      setIsOpened(false);
      return;
    }

    // Error handling and saving logic done in the parent component
    save(updatedData);
  };

  return (
    <Modal
      isOpened={isOpened}
      title={`Reorder ${title}`}
      toggle={() => setIsOpened(false)}
      focusTrap={false}
    >
      <DndProvider backend={HTML5Backend}>
        <ListContainer>
          {updatedData.map((item, index) => (
            <DraggableItem
              key={`${item[itemKey]}-${index}`}
              id={`${item[itemKey]}-${index}`}
              index={index}
              moveItem={moveItem}
              label={itemLabel(item)}
            />
          ))}
        </ListContainer>
      </DndProvider>
      {errors.main && <Callout mt mb type="error" text={errors.main} />}
      <Modal.Footer>
        <Button
          mr
          variant="secondary"
          text="Cancel"
          onClick={() => setIsOpened(false)}
        />
        <Button
          text="Save"
          onClick={handleSave}
          status={saving ? "retrieving" : "ready"}
          affirmationText="Saving"
        />
      </Modal.Footer>
    </Modal>
  );
}

export default ReorderModal;
